/**
 * Контекст витрины «Все» (US-04.1): один запрос `/analytics/all` на всю
 * страницу вместо восьми запросов по разделам.
 *
 * Зачем: витрина монтирует все секции сразу, и каждая тянула свой
 * эндпоинт — браузер держит максимум 6 соединений на хост, поэтому
 * последние разделы вставали в очередь и «догружались» заметно позже.
 * Теперь секции внутри провайдера берут готовый слайс, а свой эндпоинт
 * дёргают только если в общем ответе раздела нет (`null` — так сейчас
 * приходит `snp_needs`).
 *
 * Вне провайдера (открыт тематический подраздел) поведение прежнее:
 * секция грузит свой эндпоинт сама.
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { useKato } from '../../contexts/KatoContext';
import { useYear } from '../../contexts/YearContext';
import { isHardDataKato } from '../../hardData/constants';
import { getAnalyticsAll } from '../../requests/analytics.request';
import type { AnalyticsAllResponse } from '../../types/analytics';

/** Ключи слайсов ответа `/analytics/all` = разделы «Аналитики». */
export type AnalyticsSectionKey = keyof Omit<AnalyticsAllResponse, 'year'>;

interface AnalyticsAllValue {
  /** Внутри витрины «Все» — секции не делают своих запросов. */
  enabled: boolean;
  loading: boolean;
  data: AnalyticsAllResponse | null;
}

const AnalyticsAllContext = createContext<AnalyticsAllValue>({
  enabled: false,
  loading: false,
  data: null,
});

export const AnalyticsAllProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { selectedKato } = useKato();
  const { selectedYear } = useYear();
  // Демо-КАТО (и «территория не выбрана») — секции рисуют моки hardData,
  // запрос не нужен.
  const isHardKato = isHardDataKato(selectedKato);

  /**
   * Ключ нужного ответа (территория+год). `loading` считается из него, а не
   * из отдельного состояния: эффекты детей выполняются раньше эффекта
   * провайдера, и с «ленивым» флагом секции успевали увидеть «не грузится»
   * и уйти каждая в свой эндпоинт — ровно то, от чего избавляемся.
   */
  const requestKey =
    isHardKato || selectedKato === null
      ? null
      : `${selectedKato}|${selectedYear ?? ''}`;

  const [loaded, setLoaded] = useState<{
    key: string;
    data: AnalyticsAllResponse | null;
  } | null>(null);

  const settled = loaded !== null && loaded.key === requestKey;
  const loading = requestKey !== null && !settled;
  const data = settled ? loaded.data : null;

  useEffect(() => {
    if (requestKey === null || selectedKato === null) return;
    let cancelled = false;
    getAnalyticsAll(selectedKato, selectedYear)
      .then((response) => {
        if (!cancelled) setLoaded({ key: requestKey, data: response });
      })
      .catch((err) => {
        // Общий запрос упал — секции догрузят свои эндпоинты сами.
        if (!cancelled) {
          console.error('AnalyticsAllProvider getAnalyticsAll failed', err);
          setLoaded({ key: requestKey, data: null });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [requestKey, selectedKato, selectedYear]);

  const value = useMemo(
    () => ({ enabled: true, loading, data }),
    [loading, data],
  );

  return (
    <AnalyticsAllContext.Provider value={value}>
      {children}
    </AnalyticsAllContext.Provider>
  );
};

export const useAnalyticsAll = (): AnalyticsAllValue =>
  useContext(AnalyticsAllContext);
